import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, ShieldCheck, Crown, Gavel, Users2, Eye, Lock, Copy } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_META = {
  super_admin:  { label: "Super Admin",  color: "#E32424", icon: Crown,       desc: "Always has full access (locked)." },
  league_admin: { label: "League Admin", color: "#00A86B", icon: ShieldCheck, desc: "Operations & content." },
  court_umpire: { label: "Court Umpire", color: "#F4B400", icon: Gavel,       desc: "Live match scoring only." },
  owner:        { label: "Team Owner",   color: "#7DD3FC", icon: Users2,      desc: "Auction bidding + own team." },
  spectator:    { label: "Spectator",    color: "#9CA3AF", icon: Eye,         desc: "Read-only viewer." },
};

const NAV_LABELS = {
  dashboard: "Dashboard", spectator: "Live Broadcast", auction: "Live Auction",
  matches: "Matches", schedule: "Schedule", bracket: "Bracket",
  players: "Players", registrations: "Registrations", teams: "Teams",
  operations: "Operations", meetings: "Owners Meetings", jerseys: "Branding",
  prize_pool: "Prize Pool", sponsor_inquiries: "Sponsor Leads",
  users: "User Management", cms: "Site Content", audit_logs: "Audit Logs",
  exports: "Exports", role_permissions: "Role Permissions", admin_config: "Master Config",
};

export default function RolePermissions() {
  const [matrix, setMatrix] = useState(null);
  const [navKeys, setNavKeys] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/role-permissions");
        setMatrix(data.permissions);
        setNavKeys(data.nav_keys);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const toggle = (role, key) => {
    if (role === "super_admin") return; // locked
    setMatrix((prev) => ({ ...prev, [role]: { ...prev[role], [key]: !prev[role]?.[key] } }));
  };

  const cloneFrom = (targetRole, sourceRole) => {
    if (!sourceRole || targetRole === "super_admin") return;
    setMatrix((prev) => {
      const src = prev[sourceRole] || {};
      const next = {};
      navKeys.forEach((k) => { next[k] = !!src[k]; });
      return { ...prev, [targetRole]: next };
    });
    toast.success(`Copied ${ROLE_META[sourceRole].label} permissions → ${ROLE_META[targetRole].label}. Review & Save.`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/role-permissions", { permissions: matrix });
      // Sync local state with server-merged response (super_admin lockout enforcement, etc.)
      setMatrix(data);
      toast.success("Role permissions saved · users will see changes on next refresh");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  if (!matrix) return <div className="p-10 text-zinc-400">Loading role matrix…</div>;

  const roles = Object.keys(ROLE_META);

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> RBAC Matrix</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white" data-testid="role-perms-title">Role Permissions</h1>
          <div className="text-xs text-zinc-400 mt-1 max-w-2xl">Toggle which sidebar items each role can see. Super Admin is locked to full access to prevent system lockout.</div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest h-11" data-testid="role-perms-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Matrix"}
        </Button>
      </div>

      {/* Role overview cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {roles.map((r) => {
          const meta = ROLE_META[r];
          const Icon = meta.icon;
          const enabled = Object.values(matrix[r] || {}).filter(Boolean).length;
          return (
            <Card key={r} className="p-4 bg-[#11241b] border-white/10" data-testid={`role-card-${r}`} style={{ borderTop: `3px solid ${meta.color}` }}>
              <Icon className="w-5 h-5" style={{ color: meta.color }} />
              <div className="font-anton text-lg uppercase tracking-tight text-white mt-2">{meta.label}</div>
              <div className="text-[11px] text-zinc-400 mt-1">{meta.desc}</div>
              <div className="mt-2 text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{enabled} / {navKeys.length} unlocked</div>
              {r !== "super_admin" && (
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <Select value="" onValueChange={(v) => cloneFrom(r, v)}>
                    <SelectTrigger data-testid={`clone-from-${r}`} className="h-8 text-[11px] bg-[#060d09] border-white/10 text-zinc-300">
                      <span className="flex items-center gap-1.5"><Copy className="w-3 h-3" /> Clone from…</span>
                    </SelectTrigger>
                    <SelectContent className="bg-[#11241b] border-white/10 text-white">
                      {roles.filter((x) => x !== r).map((x) => (
                        <SelectItem key={x} value={x} data-testid={`clone-source-${r}-${x}`} className="text-[12px]">{ROLE_META[x].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Matrix */}
      <Card className="bg-[#11241b] border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="role-perms-matrix">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="px-4 py-3 text-left">Feature</th>
                {roles.map((r) => (
                  <th key={r} className="px-3 py-3 text-center min-w-[110px]">
                    <div className="font-bold" style={{ color: ROLE_META[r].color }}>{ROLE_META[r].label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {navKeys.map((key) => (
                <tr key={key} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`role-perms-row-${key}`}>
                  <td className="px-4 py-3 text-white">{NAV_LABELS[key] || key}</td>
                  {roles.map((r) => {
                    const checked = !!matrix[r]?.[key];
                    const locked = r === "super_admin";
                    return (
                      <td key={r} className="px-3 py-2 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            onChange={() => toggle(r, key)}
                            data-testid={`role-perm-${r}-${key}`}
                            className="w-4 h-4 accent-[#00A86B] disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
